import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { PageShell } from "../components/Layout/PageShell";
import { TenantMark } from "../components/TenantMark/TenantMark";
import { SignupScene } from "../components/SignupScene/SignupScene";
import { TextField } from "../components/TextField/TextField";
import { Checkbox } from "../components/Checkbox/Checkbox";
import { Button } from "../components/Button/Button";
import { InlineNotice } from "../components/InlineNotice/InlineNotice";
import { Spinner } from "../components/Spinner/Spinner";
import { ConfirmMark } from "../components/ConfirmMark/ConfirmMark";
import { getPublicTenant, joinTenant } from "../lib/api/public";
import { isValidEmail } from "../lib/validation";
import { readableTextColor, sanitizeTenantColor } from "../lib/colorUtils";
import { ApiError } from "../lib/apiClient";
import type { Tenant } from "../types";
import styles from "./JoinPage.module.css";

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  tenantContact?: string;
}

type TenantLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; tenant: Tenant };

export function JoinPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [tenantState, setTenantState] = useState<TenantLoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setTenantState({ status: "loading" });
    getPublicTenant(slug)
      .then(({ tenant }) => {
        if (!cancelled) setTenantState({ status: "ready", tenant });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError && err.status === 404
            ? "We couldn't find that business. Check the link or QR code and try again."
            : err instanceof Error
              ? err.message
              : "Something went wrong loading this page.";
        setTenantState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <PageShell>
      <div className={styles.page}>
        <SignupScene theme={tenantState.status === "ready" ? tenantState.tenant.signupTheme : "counter"} />
        <div className={styles.column}>
          {tenantState.status === "loading" && (
            <div className={styles.centerState}>
              <Spinner label="Loading" />
            </div>
          )}
          {tenantState.status === "error" && (
            <div className={styles.centerState}>
              <InlineNotice tone="error">{tenantState.message}</InlineNotice>
            </div>
          )}
          {tenantState.status === "ready" && <JoinForm slug={slug} tenant={tenantState.tenant} />}
        </div>
      </div>
    </PageShell>
  );
}

function JoinForm({ slug, tenant }: { slug: string; tenant: Tenant }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [tenantContact, setTenantContact] = useState(false);
  const [networkOffers, setNetworkOffers] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [completedAt, setCompletedAt] = useState<number | null>(null);

  const accentColor = sanitizeTenantColor(tenant.primaryColor);
  const accentInk = readableTextColor(accentColor, "#201c17", "#ffffff");
  const accentButtonStyle = {
    "--btn-primary-bg": accentColor,
    "--btn-primary-fg": accentInk,
  } as CSSProperties;

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (firstName.trim().length === 0) next.firstName = "Enter a first name.";
    if (lastName.trim().length === 0) next.lastName = "Enter a last name.";
    if (!isValidEmail(email)) next.email = "Enter a valid email address.";
    if (!tenantContact) next.tenantContact = `Agree to let ${tenant.name} contact you to join.`;
    return next;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const fieldErrors = validate();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await joinTenant(slug, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        consents: { tenantContact: true, networkOffers },
      });
      setCompletedAt(Date.now());
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Couldn't record your signup. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className={styles.identityRow}>
        <TenantMark name={tenant.name} color={tenant.primaryColor} logoUrl={tenant.logoUrl} />
      </div>
      {completedAt !== null ? (
        <div className={styles.card}>
          <div className={styles.centerState}>
            <ConfirmMark key={completedAt} />
            <p className={styles.resultText}>
              You're signed up with <strong>{tenant.name}</strong>. They may contact you about your membership.
              {networkOffers &&
                " You asked to hear about future network offers. No profile fields will be shared with another business without a separate grant."}
            </p>
          </div>
        </div>
      ) : (
        <div className={styles.card}>
          <h1 className={styles.heading}>{tenant.signupHeadline}</h1>
          <p className={styles.subheading}>{tenant.welcomeMessage}</p>
          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div className={styles.nameRow}>
              <TextField
                label="First name"
                name="firstName"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={errors.firstName}
                required
              />
              <TextField
                label="Last name"
                name="lastName"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={errors.lastName}
                required
              />
            </div>
            <TextField
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              required
            />
            <fieldset className={styles.consents}>
              <legend className={styles.consentsLabel}>Consent</legend>
              <Checkbox
                title={`Let ${tenant.name} contact me about my loyalty account`}
                required
                checked={tenantContact}
                onChange={(e) => setTenantContact(e.target.checked)}
                error={errors.tenantContact}
              />
              <Checkbox
                title="Eligible for future offers from participating local businesses"
                description="Optional. No profile information is shared without your separate permission."
                checked={networkOffers}
                onChange={(e) => setNetworkOffers(e.target.checked)}
              />
            </fieldset>
            {submitError && (
              <div className={styles.errorSlot}>
                <InlineNotice tone="error">{submitError}</InlineNotice>
              </div>
            )}
            <div className={styles.submitRow}>
              <Button type="submit" fullWidth busy={submitting} style={accentButtonStyle}>
                {submitting ? "Signing you up…" : "Sign up"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
